import apiClient from './api';

export const propertyService = {
  // Ottiene tutte le proprietà (paginato)
  // Response: ApiResponse<PageResponse<PropertyDto>>
  async getProperties(page = 0, size = 10, sort = 'name', direction = 'asc') {
    const response = await apiClient.get('/properties', {
      params: { page, size, sort, direction }
    });
    // response.data = ApiResponse { data: PageResponse }
    return response.data.data;
  },

  // Ottiene una singola proprietà
  // Response: ApiResponse<PropertyDto>
  // { silenziosa: true }: senza la notifica degli errori. La scheda rilegge cosi' la sola versione dopo un'azione che
  // scrive la struttura con un'altra chiamata (F17): un errore li' non lo deve dire nessuno.
  async getProperty(id, { silenziosa = false } = {}) {
    const response = silenziosa
      ? await apiClient.get(`/properties/${id}`, { skipToast: true })
      : await apiClient.get(`/properties/${id}`);
    return response.data.data;
  },

  /**
   * I numeri della scheda struttura in una chiamata: occupazione, listino, movimenti, rendita.
   *
   * Senza date la finestra è da oggi ai prossimi trenta giorni. La risposta dichiara in
   * `permessi` quali blocchi contiene: il ruolo di chi guarda decide cosa c'è dentro, quindi
   * NON si deve dare per scontato che `soldi` o `prezzi` ci siano.
   */
  async getRiepilogo(propertyId, dal = null, al = null) {
    const params = {};
    if (dal) params.dal = dal;
    if (al) params.al = al;
    const response = await apiClient.get(`/properties/${propertyId}/riepilogo`, { params });
    return response.data.data;
  },

  // Crea una nuova proprietà
  // Response: ApiResponse<PropertyDto>
  async createProperty(property) {
    const response = await apiClient.post('/properties', property);
    return response.data.data;
  },

  // Aggiorna una proprietà
  // Il corpo puo' portare la `version` letta (F17): se nel frattempo qualcun altro ha salvato la struttura, 409
  // STRUTTURA_MODIFICATA e niente scritto. Senza, il salvataggio di sempre.
  // Response: ApiResponse<PropertyDto>
  async updateProperty(id, property) {
    const response = await apiClient.put(`/properties/${id}`, property);
    return response.data.data;
  },

  /**
   * Copia alcune voci dei costi fissi (gli addebiti automatici all'ospite, fase2-addebiti G) su altre strutture:
   * corpo { strutture: [id], voci: [descrizione] }. In ogni struttura la voce con la stessa chiave (chiaveVoce di
   * common/gestione.js) si sostituisce, le altre si aggiungono in coda; tutto o niente. E' una chiamata del nucleo,
   * perche' le strutture le scrive solo il nucleo; la usa la finestra "Applica ad altre strutture" del modulo.
   * Response: ApiResponse<{ esiti: [{ propertyId, struttura, aggiunte, sostituite }] }>
   */
  async copiaCostiFissi(id, corpo) {
    const response = await apiClient.post(`/properties/${id}/costi-fissi/copia`, corpo);
    return response.data.data;
  },

  // Elimina definitivamente una proprietà
  // Response: ApiResponse<Void>
  async deleteProperty(id) {
    const response = await apiClient.delete(`/properties/${id}`);
    return response.data;
  },

  // Attiva/Disattiva una proprietà
  // Response: ApiResponse<PropertyDto>
  async toggleActive(id, active) {
    const response = await apiClient.patch(`/properties/${id}/active`, null, {
      params: { active }
    });
    return response.data.data;
  },

  // Ottiene le prenotazioni di una proprietà (paginato)
  // Response: ApiResponse<PageResponse<ReservationDto>>
  // includeCancelled: le annullate sono nascoste di norma, l'elenco serve a sapere chi arriva.
  async getReservations(propertyId, from, to, page = 1, size = 10, includeCancelled = false) {
    const response = await apiClient.get(`/properties/${propertyId}/reservations`, {
      params: { from, to, page, size, includeCancelled }
    });
    return response.data.data;
  },

  // Ottiene una singola prenotazione
  // Response: ApiResponse<ReservationDto>
  async getReservation(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.get(`/properties/${propertyId}/reservations/${encodedId}`);
    return response.data.data;
  },

  // Trigger sync from provider
  async syncReservations(propertyId, from, to) {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiClient.post(`/properties/${propertyId}/reservations/sync`, null, { params });
    return response.data.data;
  },

  // Create manual reservation
  async createReservation(propertyId, data) {
    const response = await apiClient.post(`/properties/${propertyId}/reservations`, data);
    return response.data.data;
  },

  // Modifica una prenotazione manuale: date, camera, dati dell'ospite.
  // I campi assenti o vuoti non vengono toccati.
  async updateReservation(propertyId, reservationId, data) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.put(`/properties/${propertyId}/reservations/${encodedId}`, data);
    return response.data.data;
  },

  // Cancel reservation
  async cancelReservation(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.delete(`/properties/${propertyId}/reservations/${encodedId}/cancel`);
    return response.data.data;
  },

  // Ottiene i dettagli raw della prenotazione dal provider
  // Response: ApiResponse<Map>
  async getReservationDetails(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.get(`/properties/${propertyId}/reservations/${encodedId}/details`);
    return response.data.data;
  },

  // Ottiene l'access link per una prenotazione (se esiste)
  // Response: ApiResponse<AccessLinkDto | null>
  async getAccessLink(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.get(`/properties/${propertyId}/reservations/${encodedId}/access-link`);
    return response.data.data;
  },

  // Crea un access link per una prenotazione (restituisce esistente se già creato)
  // Response: ApiResponse<AccessLinkDto>
  async createAccessLink(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.post(`/properties/${propertyId}/reservations/${encodedId}/access-link`);
    return response.data.data;
  },

  // Rigenera l'access link per una prenotazione (revoca il vecchio)
  // Response: ApiResponse<AccessLinkDto>
  async regenerateAccessLink(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.put(`/properties/${propertyId}/reservations/${encodedId}/access-link`);
    return response.data.data;
  },

  // Aggiorna i custom times dell'access link
  // Response: ApiResponse<AccessLinkDto>
  async updateAccessLink(propertyId, reservationId, data) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.patch(`/properties/${propertyId}/reservations/${encodedId}/access-link`, data);
    return response.data.data;
  },

  // Revoca l'access link per una prenotazione
  // Response: ApiResponse<Void>
  async revokeAccessLink(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.delete(`/properties/${propertyId}/reservations/${encodedId}/access-link`);
    return response.data;
  },

  // Sincronizza un codice accesso sul dispositivo
  // Response: ApiResponse<AccessCodeDto>
  async syncAccessCode(propertyId, reservationId, codeId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.post(`/properties/${propertyId}/reservations/${encodedId}/access-link/codes/${codeId}/sync`);
    return response.data.data;
  },

  // Ottiene i conflitti per i codici di una prenotazione
  // Response: ApiResponse<List<AccessCodeConflictDto>>
  async getAccessCodeConflicts(propertyId, reservationId) {
    const encodedId = encodeURIComponent(reservationId);
    const response = await apiClient.get(`/properties/${propertyId}/reservations/${encodedId}/access-link/conflicts`);
    return response.data.data;
  }
};

export default propertyService;
